import { isFriday, parseISO } from 'date-fns';
import { CalendarOff, Plus } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { formatDate, formatDisplayDate } from '@/utils/date';
import { getPreferredScrollBehavior } from '@/utils/motion';
import {
  getMonthCalendarWeeks,
  getWeekdayLabels,
  type MonthCalendarDay,
} from '@/utils/monthCalendar';
import {
  HOLIDAY_CHIP_CLASS,
  HOLIDAY_MARK_CLASS,
  OUT_OF_MONTH_CELL_CLASS,
  WEEKEND_CHIP_CLASS,
} from '../constants/holiday.constants';
import type { Holiday } from '../types/holiday.types';
import { formatHolidayDates, getHolidaysOnDay } from '../utils/holidayCalendar';

const WEEKDAY_LABELS = getWeekdayLabels();

const DAY_CELL_CLASS =
  'relative flex min-h-20 min-w-0 flex-col gap-1.5 p-2 sm:min-h-28 lg:min-h-0 lg:overflow-hidden';
const CHIP_CLASS = 'h-7 w-full px-2 text-left text-xs font-medium';
const TODAY_NUMBER_CLASS = `font-semibold ${ACCENT_MARK_CLASS}`;
const OPENED_DAY_FLASH_CLASS =
  'pointer-events-none absolute inset-0 bg-primary/10 ring-2 ring-primary ring-inset transition-opacity duration-700 ease-out motion-reduce:transition-none';

interface HolidayMonthCalendarProps {
  monthStart: Date;
  holidays: Holiday[] | undefined;
  openedDate?: string | undefined;
  isOpenedDateFlashing: boolean;
  onAddHoliday: (isoDate: string) => void;
  onEditHoliday: (holiday: Holiday) => void;
}

export function HolidayMonthCalendar({
  monthStart,
  holidays,
  openedDate,
  isOpenedDateFlashing,
  onAddHoliday,
  onEditHoliday,
}: HolidayMonthCalendarProps) {
  const today = formatDate(new Date());

  return (
    <section aria-label="Holiday calendar" className="flex min-w-0 flex-col lg:min-h-0 lg:flex-1">
      <div className="grid grid-cols-7 border-b" aria-hidden>
        {WEEKDAY_LABELS.map((weekday) => (
          <span
            key={weekday}
            className="py-2.5 text-center text-xs font-medium text-muted-foreground"
          >
            {weekday}
          </span>
        ))}
      </div>
      <div className="flex flex-col divide-y lg:min-h-0 lg:flex-1">
        {getMonthCalendarWeeks(monthStart).map((week) => (
          <div
            key={week[0]?.isoDate}
            className="grid grid-cols-7 divide-x lg:min-h-0 lg:flex-1 lg:basis-0"
          >
            {week.map((day) => (
              <CalendarDayCell
                key={day.isoDate}
                day={day}
                isToday={day.isoDate === today}
                isOpened={day.isoDate === openedDate}
                isFlashing={isOpenedDateFlashing}
                holidays={holidays && getHolidaysOnDay(holidays, day.isoDate)}
                onAddHoliday={onAddHoliday}
                onEditHoliday={onEditHoliday}
              />
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

interface CalendarDayCellProps {
  day: MonthCalendarDay;
  isToday: boolean;
  isOpened: boolean;
  isFlashing: boolean;
  holidays: Holiday[] | undefined;
  onAddHoliday: (isoDate: string) => void;
  onEditHoliday: (holiday: Holiday) => void;
}

function CalendarDayCell({
  day,
  isToday,
  isOpened,
  isFlashing,
  holidays,
  onAddHoliday,
  onEditHoliday,
}: CalendarDayCellProps) {
  const dayNumber = <DayNumber day={day} isToday={isToday} />;

  if (!day.isInMonth) {
    return (
      <div className={cn(DAY_CELL_CLASS, OUT_OF_MONTH_CELL_CLASS)} aria-hidden>
        {dayNumber}
      </div>
    );
  }

  const openedDayFlash = isOpened ? <OpenedDayFlash isFlashing={isFlashing} /> : null;

  if (isFriday(parseISO(day.isoDate))) {
    return (
      <div className={DAY_CELL_CLASS}>
        {dayNumber}
        <WeekendChip />
        {openedDayFlash}
      </div>
    );
  }

  if (holidays === undefined) {
    return (
      <div className={DAY_CELL_CLASS}>
        {dayNumber}
        <Skeleton className="h-7 w-full rounded-md max-sm:hidden" />
        {openedDayFlash}
      </div>
    );
  }

  if (holidays.length > 0) {
    return (
      <div className={DAY_CELL_CLASS}>
        {dayNumber}
        {holidays.map((holiday) => (
          <HolidayChip key={holiday.id} holiday={holiday} onEditHoliday={onEditHoliday} />
        ))}
        {openedDayFlash}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => onAddHoliday(day.isoDate)}
      aria-label={`Add a holiday on ${formatDisplayDate(day.isoDate)}`}
      className={cn(
        DAY_CELL_CLASS,
        'group cursor-pointer text-left transition-colors hover:bg-primary/3 focus-visible:bg-primary/3 focus-visible:outline-none',
      )}
    >
      {dayNumber}
      <AddHolidayHint />
      {openedDayFlash}
    </button>
  );
}

function scrollIntoViewOnMount(node: HTMLSpanElement | null) {
  node?.scrollIntoView({ block: 'nearest', behavior: getPreferredScrollBehavior() });
}

interface OpenedDayFlashProps {
  isFlashing: boolean;
}

function OpenedDayFlash({ isFlashing }: OpenedDayFlashProps) {
  return (
    <span
      ref={scrollIntoViewOnMount}
      aria-hidden
      className={cn(OPENED_DAY_FLASH_CLASS, isFlashing ? 'opacity-100' : 'opacity-0')}
    />
  );
}

function AddHolidayHint() {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute inset-0 flex items-center justify-center text-primary opacity-0 transition-opacity duration-200 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none max-sm:hidden"
    >
      <Plus
        className="size-5 scale-75 transition-transform duration-200 ease-out group-hover:scale-100 group-focus-visible:scale-100 motion-reduce:transition-none"
        strokeWidth={3}
      />
    </span>
  );
}

interface DayNumberProps {
  day: MonthCalendarDay;
  isToday: boolean;
}

function DayNumber({ day, isToday }: DayNumberProps) {
  return (
    <span
      className={cn(
        'flex size-6 items-center justify-center self-end rounded-full text-xs font-medium tabular-nums',
        !day.isInMonth && 'text-muted-foreground opacity-40',
        isToday && TODAY_NUMBER_CLASS,
      )}
    >
      {day.dayOfMonth}
    </span>
  );
}

function WeekendChip() {
  return (
    <span
      className={cn(CHIP_CLASS, WEEKEND_CHIP_CLASS, 'items-center gap-1.5 max-sm:hidden sm:flex')}
    >
      <CalendarOff className="size-3.5 shrink-0" aria-hidden />
      <span className="truncate">Weekend</span>
    </span>
  );
}

interface HolidayChipProps {
  holiday: Holiday;
  onEditHoliday: (holiday: Holiday) => void;
}

function HolidayChip({ holiday, onEditHoliday }: HolidayChipProps) {
  const holidayDates = formatHolidayDates(holiday);
  const tone = holiday.isActive ? 'active' : 'inactive';

  return (
    <>
      <span
        className={cn('h-1.5 w-full rounded-full sm:hidden', HOLIDAY_MARK_CLASS[tone])}
        aria-hidden
      />
      <Tooltip>
        <TooltipTrigger
          render={<button type="button" onClick={() => onEditHoliday(holiday)} />}
          aria-label={`Edit ${holiday.name}, ${holidayDates}`}
          className={cn(
            CHIP_CLASS,
            'cursor-pointer items-center gap-1.5 transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none max-sm:hidden sm:flex',
            HOLIDAY_CHIP_CLASS[tone],
          )}
        >
          {holiday.isActive ? (
            <span
              aria-hidden
              className={cn('h-3.5 w-0.75 shrink-0 rounded-full', HOLIDAY_MARK_CLASS.active)}
            />
          ) : null}
          <span className="truncate">{holiday.name}</span>
        </TooltipTrigger>
        <TooltipContent>
          <span className="flex max-w-64 flex-col gap-0.5">
            <span className="font-medium">{holiday.name}</span>
            <span>{holidayDates}</span>
            {holiday.isActive ? null : <span>Inactive, not counted as a day off</span>}
          </span>
        </TooltipContent>
      </Tooltip>
    </>
  );
}
